import { useState } from "react";
import { Alert, Button } from "reactstrap";
import useFieldTypes from "api/api-hooks/cloud/useFieldTypes";
import FieldUtils from "../../../utils/FieldUtils";
import AppSpinner from "../../../elements/AppSpinner";
import AppDrawer from "../AppDrawer";
import FieldDefault from "./FieldDefault";
import FieldSettingsGroup from "./FieldSettingsGroup";
import FieldTypeManager from "./FieldTypeSpecific/FieldTypeManager";
import FieldTypeSpecific from "./FieldTypeSpecific/FieldTypeSpecific";
import InformationGroup from "./InformationGroup";

const getInitialFieldValues = (subitemParent) => ({
  name: "",
  apiName: "",
  fieldType: "",
  description: "",
  required: false,
  unique: false,
  default: false,
  defaultValue: "",
  defaultSource: "",
  source: "",
  calculatedFieldData: {},
  trueDisplayName: "True",
  falseDisplayName: "False",
  protected: false,
  renameProtected: false,
  readonly: false,
  sensitive: false,
  summary: false,
  summaryFields: {},
  subitemFields: [],
  public: false,
  gcWebsite: false,
  hierarchical: false,
  subitemParent: subitemParent,
  presentation: "Logo",
  subitemRecordNameField: "",
  gcUsers: false,
});

// Synced with the step state inside of CreateFieldDrawer.
const stepInformation = [
  {
    title: "Field Information",
    description: "Basic information about the new field.",
  },
  {
    title: "Field Type",
    description: "Select the type of field you want to create.",
  },
  {
    title: "Field Type Specific Settings",
    description: "These settings are specific to the field type.",
  },
  {
    title: "Field Default Value",
    description: "Optionally add a default value for this field.",
  },
  {
    title: "Additional Field Settings",
    description: "Additional settings for the field.",
  },
];

/**
 * CreateFieldDrawer - Drawer for creating a new field inside of a module
 *
 * @param {*} open - Whether or not the drawer is open
 * @param {*} onClose - Function to call when the drawer is closed
 * @param {*} submitting - Whether or not the form is submitting
 * @param {*} error - Error from the form submission
 * @param {*} onSave - Function to call when the save button is clicked
 *
 * step 0 - Field Information
 * step 1 - Field Type
 * step 2 - Field Type Specific Settings
 * step 3 - Field Default Value (skipped depending on field type)
 * step 4 - Additional Field Settings
 */
const CreateFieldDrawer = ({
  open,
  onClose,
  submitting,
  onSave,
  error,
  size = "md",
  showModificationProtection = false,
  subitemParent = null, // If provided, Drawer will be a subitem drawer
  moduleAPIName,
}) => {
  const [step, setStep] = useState(0); // See step information above
  const [fieldValues, setFieldValues] = useState(
    getInitialFieldValues(subitemParent)
  );
  const { fieldTypes, fieldTypesLoading } = useFieldTypes();

  const [showError, setShowError] = useState(false);

  const handleClose = () => {
    setFieldValues(getInitialFieldValues(subitemParent));
    setStep(0);
    onClose();
  };

  // Skip FieldType specific settings if field type does not require them
  // Currently Selects, References, and SubItems require FieldType specific settings
  const checkStep2 = (newStep, amount) => {
    if (
      newStep === 2 &&
      !FieldUtils.fieldTypeHasOptions(fieldTypes, fieldValues.fieldType) &&
      !FieldUtils.fieldTypeIsSubitem(fieldValues.fieldType) &&
      ![
        "Toggle",
        "Image",
        "SingleFile",
        "Audio",
        "Video",
        "URL",
        "Filter",
      ].includes(fieldValues.fieldType)
    ) {
      newStep = newStep + amount;
    }

    return newStep;
  };

  // Skip default section if FieldType doesn't support
  const checkStep3 = (newStep, amount) => {
    if (newStep === 3 && !FieldUtils.fieldUsesDefault(fieldValues.fieldType)) {
      newStep = newStep + amount;
    }

    return newStep;
  };

  // Pass positive or negative value to go forward or backwards in steps
  const handleStep = (amount) => () => {
    if (amount > 0 && isButtonDisabled()) return;

    let newStep = step + amount;

    if (amount > 0) {
      newStep = checkStep2(newStep, amount);
      newStep = checkStep3(newStep, amount);
    } else if (amount < 1) {
      newStep = checkStep3(newStep, amount);
      newStep = checkStep2(newStep, amount);
    }

    setStep(newStep);
  };

  const handleError = () => {
    setStep(0);
    setShowError(true);
  };

  const handleSave = () => () => {
    onSave(fieldValues, handleClose, handleError);
  };

  const isButtonDisabled = () => {
    if (step === 0 && (!fieldValues.name || !fieldValues.apiName)) {
      return true;
    }

    if (step === 1 && !fieldValues.fieldType) {
      return true;
    }

    if (step === 2 && FieldUtils.fieldTypeIsReference(fieldValues.fieldType)) {
      // Ensure References have a source
      if (!fieldValues.source) {
        return true;
      }

      // Ensure there are (summary fields, summary presentation) when summary is enabled
      if (fieldValues.summary === true) {
        let source = fieldValues?.source;
        let sources = Array.isArray(source) ? source : [source];
        sources = sources?.map((s) => s.split("Modules.", 2)[1]);

        let isMissingData = false;

        // Check all reference sources for summary fields and presentation
        sources.forEach((s) => {
          if (
            fieldValues?.summaryFields?.[s]?.Fields?.length === 0 ||
            !fieldValues.summaryFields[s]?.summaryPresentation
          )
            isMissingData = true;
        });

        return isMissingData;
      }
    }

    if (
      step === 2 &&
      fieldValues.fieldType === "Filter" &&
      !fieldValues.source
    ) {
      return true;
    }

    if (step === 2 && fieldValues.fieldType === "Calculated")
      if (!fieldValues.calculatedFieldData.Formula) {
        // Ensure a valid formula
        return true;
      }
    return false;
  };

  return (
    <AppDrawer
      open={open}
      onClose={handleClose}
      headerTitle={`${stepInformation[step].title}`}
      headerDescription={stepInformation[step].description}
      backdrop="static"
      bodyCSS="px-2 pt-2 pb-8"
      size={size}
      headerOptions={
        <>
          {step > 0 && (
            <Button size="sm" onClick={handleStep(-1)}>
              Previous
            </Button>
          )}
          <Button
            size="sm"
            color="primary"
            onClick={
              step === stepInformation.length - 1 ? handleSave() : handleStep(1)
            }
            disabled={submitting || isButtonDisabled() || fieldTypesLoading}
            style={{ width: 105 }}
          >
            {step === 4 ? (
              submitting ? (
                <AppSpinner size="sm" color="white" />
              ) : (
                "Create Field"
              )
            ) : (
              "Next"
            )}
          </Button>
        </>
      }
      body={
        <div className="py-3 mb-3">
          {step === 0 && (
            <InformationGroup
              fieldValues={fieldValues}
              setFieldValues={setFieldValues}
              showModificationProtection={showModificationProtection}
              setShowError={setShowError}
              handleSubmit={handleStep(1)}
            />
          )}
          {step === 1 && (
            <FieldTypeManager
              fieldValues={fieldValues}
              setFieldValues={setFieldValues}
              disableSubitem={!!subitemParent}
              searchEnabled
              handleSubmit={handleStep(1)}
            />
          )}
          {step === 2 && (
            <FieldTypeSpecific
              fieldValues={fieldValues}
              setFieldValues={setFieldValues}
              showModificationProtection={showModificationProtection}
              moduleAPIName={moduleAPIName}
            />
          )}
          {step === 3 && (
            <FieldDefault
              fieldValues={fieldValues}
              setFieldValues={setFieldValues}
              showModificationProtection={showModificationProtection}
              disableSubitem={!!subitemParent}
            />
          )}
          {step === 4 && (
            <FieldSettingsGroup
              fieldValues={fieldValues}
              setFieldValues={setFieldValues}
              showModificationProtection={showModificationProtection}
            />
          )}
          {error && showError && (
            <div style={{ position: "absolute", bottom: 80, width: 550 }}>
              <Alert color="danger">{error}</Alert>
            </div>
          )}
        </div>
      }
    />
  );
};

export default CreateFieldDrawer;
